import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import Alert from '../../components/Alert/Alert';
import { getExpenseById } from './api/expenseApi';
import api from '../../api';

const ExpenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [expense, setExpense] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await getExpenseById(id);
        setExpense(data);
      } catch (err) {
        console.error('Error loading expense:', err);
        showAlert('danger', 'Failed to load expense detail.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount, currencyCode = 'IDR') => {
    if (!amount) return '-';
    const num = parseFloat(amount);
    if (isNaN(num)) return '-';
    const locale = currencyCode === 'USD' ? 'en-US'
      : currencyCode === 'EUR' ? 'de-DE'
      : currencyCode === 'GBP' ? 'en-GB'
      : 'id-ID';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
    }).format(num);
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`/crm/expenses/download/${id}`, { responseType: 'blob' });
      let fileName = expense.fileName || 'expense-document';
      if (fileName.includes('_')) {
        fileName = fileName.substring(fileName.lastIndexOf('_') + 1);
      }
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      showAlert('danger', 'Failed to download file.');
    }
  };

  const idrAmount = () => {
    if (!expense?.amount) return '-';
    const num = parseFloat(expense.amount);
    const code = (expense.currencyCode || 'IDR').toUpperCase();
    const rate = parseFloat(expense.exchangeRateAtCreation) || 1;
    const idr = code === 'IDR' ? num : num * rate;
    return 'Rp ' + idr.toLocaleString('id-ID', { maximumFractionDigits: 0 });
  };

  return (
    <div className="container-xl">
      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}

      <div className="row g-2 align-items-center mb-3">
        <div className="col-auto">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/expense')}>
            <ArrowLeft size={16} className="me-1" />
            Back
          </button>
        </div>
        <div className="col">
          <h3 className="page-title m-0">Expense Detail</h3>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate('/expense')}
          >
            Back to List
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : !expense ? (
        <div className="alert alert-danger">Expense not found.</div>
      ) : (
        <div className="row g-3">
          <div className="col-12 col-lg-8">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title m-0">Expense Information</h4>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">Date of Expense</label>
                    <div className="fw-medium">{formatDate(expense.expenseDate)}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">Category</label>
                    <div className="fw-medium">{expense.categoryName || '-'}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">Client / Company</label>
                    <div className="fw-medium">{expense.companyName || '-'}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">Project</label>
                    <div className="fw-medium">{expense.projectName || '-'}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">Currency</label>
                    <div className="fw-medium">{expense.currencyCode || 'IDR'}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">Amount</label>
                    <div className="fw-medium">
                      {formatCurrency(expense.amount, expense.currencyCode || 'IDR')}
                      {(expense.currencyCode || 'IDR').toUpperCase() !== 'IDR' && (
                        <span className="text-muted small ms-2">
                          ≈ {idrAmount()}
                        </span>
                      )}
                    </div>
                  </div>
                  {(expense.currencyCode || 'IDR').toUpperCase() !== 'IDR' && (
                    <div className="col-md-6">
                      <label className="form-label text-muted small mb-1">Exchange Rate</label>
                      <div className="fw-medium">
                        {expense.exchangeRateAtCreation
                          ? `1 ${expense.currencyCode} = Rp ${parseFloat(expense.exchangeRateAtCreation).toLocaleString('id-ID')}`
                          : '-'}
                      </div>
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label text-muted small mb-1">Description</label>
                    <div className="fw-medium">{expense.description || '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title m-0">Attached Document</h4>
              </div>
              <div className="card-body">
                {expense.fileName ? (
                  <div className="d-flex align-items-center gap-2 p-3 border rounded">
                    <FileText size={32} className="text-danger flex-shrink-0" />
                    <div className="flex-grow-1 min-width-0">
                      <div
                        className="fw-medium text-truncate"
                        title={expense.fileName.includes('_')
                          ? expense.fileName.substring(expense.fileName.lastIndexOf('_') + 1)
                          : expense.fileName}
                      >
                        {expense.fileName.includes('_')
                          ? expense.fileName.substring(expense.fileName.lastIndexOf('_') + 1)
                          : expense.fileName}
                      </div>
                      <small className="text-muted">PDF Document</small>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm flex-shrink-0"
                      onClick={handleDownload}
                    >
                      Download
                    </button>
                  </div>
                ) : (
                  <div className="text-muted text-center py-3">No document attached</div>
                )}
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">
                <h4 className="card-title m-0">Summary</h4>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">IDR Equivalent</span>
                  <span className="fw-bold text-success">{idrAmount()}</span>
                </div>
                {(expense.currencyCode || 'IDR').toUpperCase() !== 'IDR' && (
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted small">Original Amount</span>
                    <span className="fw-medium">
                      {formatCurrency(expense.amount, expense.currencyCode)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseDetail;
